import React, { useState, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import SEO from '../components/SEO'
import LightboxModal from '../components/LightboxModal'
import { useCms } from '../context/CmsContext'
import { GALLERY_ITEMS } from '../data'

// Helper for responsive CDN images
function getResizedImageUrl(url, width = 600) {
  if (!url || typeof url !== 'string') return url
  if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`)
  }
  return url
}

export default function GalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeStyle = searchParams.get('stil') || 'all'

  const { tattooStyles, galleries, getSection, getPage } = useCms()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const pageData = getPage('galleri', {})

  const heroSection = {
    title: pageData.heroTitle || getSection('gallery_hero').title || 'Tidligere Arbejde & Skitser',
    eyebrow: pageData.heroEyebrow || getSection('gallery_hero').eyebrow || 'Galleri',
    subtitle: pageData.heroSubtitle || getSection('gallery_hero').subtitle || 'Hver tatovering hos Docuit ink er udført med kunstnerisk præcision. Klik på et billede for at åbne det i stor visning.'
  }

  // Compute all gallery items: combining CMS tattoo_styles + CMS galleries + data.js fallback
  const items = useMemo(() => {
    const combinedList = []
    const seenUrls = new Set()

    // 1. Add all images from CMS tattoo_styles collection
    if (tattooStyles && tattooStyles.length > 0) {
      tattooStyles.forEach((style) => {
        const styleImgs = Array.isArray(style.images) && style.images.length > 0
          ? style.images
          : (style.image ? [style.image] : [])

        styleImgs.forEach((imgUrl, i) => {
          if (imgUrl && typeof imgUrl === 'string' && !seenUrls.has(imgUrl)) {
            seenUrls.add(imgUrl)
            combinedList.push({
              id: `style-${style.id || style.slug}-${i}`,
              title: style.title,
              style: style.id,
              styleId: style.id,
              styleSlug: style.slug,
              styleName: style.title,
              src: imgUrl,
              alt: `${style.title} tatovering udført af Docuit ink`,
              description: style.tagline || style.description || '',
              placement: ''
            })
          }
        })
      })
    }

    // 2. Add all images from CMS galleries collection
    if (galleries && galleries.length > 0) {
      galleries.forEach((g) => {
        const gImages = Array.isArray(g.images)
          ? g.images
          : (g.data?.image ? (Array.isArray(g.data.image) ? g.data.image : [g.data.image]) : [])
        const altList = (g.alt_texts || g.data?.alt || '').split(',').map(s => s.trim())
        
        gImages.forEach((imgUrl, i) => {
          if (imgUrl && typeof imgUrl === 'string' && !seenUrls.has(imgUrl)) {
            seenUrls.add(imgUrl)
            
            const gTitle = g.title || ''
            const gSlug = g.page_slug || g.slug?._ || g.slug || ''
            const matchedStyle = tattooStyles.find(st => 
              st.id === g.id ||
              st.slug === gSlug ||
              (st.title && gTitle && st.title.toLowerCase().trim() === gTitle.toLowerCase().trim()) ||
              (g.terms?.['tattoo-style'] && g.terms['tattoo-style'].some(term => st.slug === term || st.id === term || (term === 'oldschool-realistisk' && (st.slug.includes('oldschool') || st.title.toLowerCase().includes('oldschool')))))
            )

            combinedList.push({
              id: `gallery-${g.id || 'item'}-${i}`,
              title: g.title || matchedStyle?.title || 'Tatovering',
              style: matchedStyle ? matchedStyle.id : (g.page_slug || 'all'),
              styleId: matchedStyle ? matchedStyle.id : (g.id || 'all'),
              styleSlug: matchedStyle ? matchedStyle.slug : (g.page_slug || ''),
              styleName: matchedStyle ? matchedStyle.title : (g.title || 'Tatovering'),
              src: imgUrl,
              alt: altList[i] || g.title || 'Tatovering af Docuit ink',
              description: g.description || matchedStyle?.description || ''
            })
          }
        })
      })
    }

    // 3. Fallback to GALLERY_ITEMS for any remaining unique items
    GALLERY_ITEMS.forEach((fallbackItem, idx) => {
      if (fallbackItem.src && !seenUrls.has(fallbackItem.src)) {
        seenUrls.add(fallbackItem.src)
        const matchedStyle = tattooStyles.find(st => 
          st.id === fallbackItem.style ||
          st.slug === fallbackItem.style ||
          st.slug.includes(fallbackItem.style) ||
          fallbackItem.style?.includes(st.slug) ||
          (fallbackItem.styleName && st.title.toLowerCase().includes(fallbackItem.styleName.toLowerCase()))
        )
        combinedList.push({
          ...fallbackItem,
          id: fallbackItem.id || `fallback-${idx}`,
          style: matchedStyle ? matchedStyle.id : fallbackItem.style,
          styleId: matchedStyle ? matchedStyle.id : fallbackItem.style,
          styleSlug: matchedStyle ? matchedStyle.slug : fallbackItem.style,
          styleName: matchedStyle ? matchedStyle.title : fallbackItem.styleName
        })
      }
    })

    return combinedList
  }, [tattooStyles, galleries])

  // Find active style object for intelligent filtering
  const selectedStyleObj = useMemo(() => {
    if (activeStyle === 'all') return null
    return tattooStyles.find(s => 
      s.id === activeStyle ||
      s.slug === activeStyle ||
      s.styleId === activeStyle ||
      s.entryId === activeStyle ||
      (s.slug && (s.slug.includes(activeStyle) || activeStyle.includes(s.slug))) ||
      (s.title && s.title.toLowerCase() === activeStyle.toLowerCase()) ||
      (s.title && activeStyle.replace(/[^a-z0-9]+/gi, '').toLowerCase().includes(s.title.replace(/[^a-z0-9]+/gi, '').toLowerCase()))
    )
  }, [activeStyle, tattooStyles])

  const matchesStyle = (item, styleObj, query) => {
    if (!item) return false
    if (styleObj) {
      if (item.styleId === styleObj.id || item.styleId === styleObj.styleId || item.styleId === styleObj.entryId) return true
      if (item.styleSlug && (item.styleSlug === styleObj.slug || item.styleSlug.includes(styleObj.slug) || styleObj.slug?.includes(item.styleSlug))) return true
      if (item.style === styleObj.id || item.style === styleObj.slug) return true
      if (item.styleName && styleObj.title && item.styleName.toLowerCase().trim() === styleObj.title.toLowerCase().trim()) return true
    }
    if (!query || query === 'all') return true
    return (
      item.style === query ||
      item.styleId === query ||
      item.styleSlug === query ||
      (item.styleSlug && (item.styleSlug.includes(query) || query.includes(item.styleSlug))) ||
      (item.style && typeof item.style === 'string' && (item.style.includes(query) || query.includes(item.style))) ||
      (item.styleName && item.styleName.toLowerCase().trim() === query.toLowerCase().trim()) ||
      (item.styleName && item.styleName.toLowerCase().replace(/[^a-z0-9]+/g, '').includes(query.toLowerCase().replace(/[^a-z0-9]+/g, '')))
    )
  }

  const filteredItems = useMemo(() => {
    if (activeStyle === 'all') return items
    return items.filter((item) => matchesStyle(item, selectedStyleObj, activeStyle))
  }, [items, activeStyle, selectedStyleObj])

  const openLightbox = (idx) => {
    setLightboxIndex(idx)
    setLightboxOpen(true)
  }

  return (
    <div className="min-h-screen bg-[#0f1013] text-[#f4f3f0] py-12 md:py-16">
      <SEO
        title="Galleri | Se Tidligere Tatoveringer & Skitser - Docuit ink"
        description="Gå på opdagelse i mit galleri med billeder af udførte tatoveringer hos Docuit ink i Vester Sottrup nær Sønderborg. Find inspiration til dit næste unikke motiv."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-extrabold text-white mb-4">
            {heroSection.title}
          </h1>
          {heroSection.subtitle && (
            <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
              {heroSection.subtitle}
            </p>
          )}
        </div>

        {/* Filter Category Tabs */}
        <div className="flex items-center justify-center flex-wrap gap-2.5 mb-12">
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors cursor-pointer ${
              activeStyle === 'all'
                ? 'bg-white text-neutral-950 font-bold shadow-md'
                : 'bg-[#17191e] border border-[#282b34] text-neutral-300 hover:text-white hover:border-neutral-600'
            }`}
          >
            Alle ({items.length})
          </button>
          {tattooStyles.map((style) => {
            const isSelected = activeStyle === style.id || activeStyle === style.slug || (selectedStyleObj && selectedStyleObj.id === style.id)
            const styleCount = items.filter(item => matchesStyle(item, style, style.id)).length

            return (
              <button
                key={style.id}
                type="button"
                onClick={() => setSearchParams({ stil: style.id })}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors cursor-pointer ${
                  isSelected
                    ? 'bg-white text-neutral-950 font-bold shadow-md'
                    : 'bg-[#17191e] border border-[#282b34] text-neutral-300 hover:text-white hover:border-neutral-600'
                }`}
              >
                {style.title} {styleCount > 0 && <span className="opacity-70 ml-1">({styleCount})</span>}
              </button>
            )
          })}
        </div>

        {/* Gallery Grid */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-16 bg-[#17191e] border border-[#282b34] rounded-lg">
            <i className="fa-solid fa-camera text-4xl text-neutral-600 mb-3" aria-hidden="true" />
            <p className="text-neutral-400 text-sm">Ingen tatoveringer fundet for den valgte kategori.</p>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="mt-4 px-4 py-2 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] rounded-full text-xs font-bold uppercase transition-colors cursor-pointer"
            >
              Vis alle tatoveringer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems.map((img, idx) => (
              <div
                key={img.id}
                onClick={() => openLightbox(idx)}
                className="group relative bg-[#17191e] border border-[#282b34] rounded-lg overflow-hidden cursor-pointer hover:border-[#cbb05e] transition-all flex flex-col shadow-md"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openLightbox(idx)
                }}
                aria-label={`Forstør ${img.title}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-neutral-900 relative">
                  <img
                    src={getResizedImageUrl(img.src, 600)}
                    alt={img.alt || img.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-[#cbb05e] text-[#0f1013] flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                      <i className="fa-solid fa-magnifying-glass text-sm" aria-hidden="true" />
                    </span>
                  </div>
                </div>
                <div className="p-3.5 flex-1 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] font-mono text-[#cbb05e] uppercase tracking-wider block mb-1">
                      {img.styleName || img.style}
                    </span>
                    <h3 className="font-serif font-bold text-white text-sm">
                      {img.title}
                    </h3>
                  </div>
                  {img.placement && (
                    <span className="text-[11px] text-neutral-400 mt-2 block">
                      Placering: {img.placement}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Direct Booking CTA */}
        <div className="mt-16 text-center bg-[#17191e] border border-[#282b34] rounded-xl p-8 sm:p-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-white mb-3">
            {pageData.ctaTitle || 'Inspireret af mine tatoveringer?'}
          </h2>
          <p className="text-neutral-400 text-sm max-w-xl mx-auto mb-6">
            {pageData.ctaSubtitle || 'Lad os tage en snak om dit næste projekt. Udfyld bookingformularen med dine ønsker eller ring direkte.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] font-bold uppercase tracking-wider text-xs rounded-full transition-colors shadow-lg"
            >
              {pageData.ctaButtonText || 'Send bookingforespørgsel'}
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>

      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        currentIndex={lightboxIndex}
        images={filteredItems}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={(newIdx) => setLightboxIndex(newIdx)}
      />
    </div>
  )
}
